import PageCard from "features/shared/components/card/PageCard";
import FormGroup from "features/shared/forms/components/FormGroup";
import RadioBox from "features/shared/forms/components/radioBox/RadioBox";
import { UseFormHookResult } from "features/shared/forms/hooks/useForm";
import React, { ChangeEvent } from "react";
import {
  mapPaymentTypeCode,
} from "features/contracting/sections/contractingForms/paymentMethodForm/paymentMethodFormHelper";
import { useContractingDialog } from "../hooks/useContractingDialog";
import { AlertType } from "features/shared/alerts/enums/alertType";
import { useTranslationWithPrefix } from "features/shared/hooks/useTranslationWithPrefix";

type Props = {
  paymentTypes: string[];
  formHandler: UseFormHookResult;
  onlyInvoicePaymentTypes?: boolean;
};

const PaymentMethodTypeSubForm = ({
  paymentTypes,
  formHandler,
  onlyInvoicePaymentTypes = false,
}: Props) => {
  // translations
  const tLabels = useTranslationWithPrefix(
    "contracting\\contracting",
    "paymentMethodTypeSubForm"
  );

  const { openInfoDialog, closeInfoDialog } = useContractingDialog();

  // form hook props
  const {
    errors,
    getFormValue,
    setFormValue,
    validateFormField,
    values,
    clearFormValues,
  } = {
    ...formHandler,
  };

  // handlers
  const paymentTypeChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const name = String(e.target.name);
    const value = e.target.value;
    // setting selected method (mainly for ) depositPaymentType
    setFormValue(name, value);

    if (value !== "Sipo") {
      try {
        // automatic setting of invoicePaymentType and overpaidPaymentType
        // deposit codes must be mapped to invoice/overpaid codes
        const invoicePaymentTypeCode = mapPaymentTypeCode(value, "Invoice");
        setFormValue("invoicePaymentTypeCode", invoicePaymentTypeCode);

        const overpaidPaymentTypeCode = mapPaymentTypeCode(value, "Overpaid");
        setFormValue("overpaidPaymentTypeCode", overpaidPaymentTypeCode);
      } catch (error) {
        // in case of missing mapping show error
        openInfoDialog("processingErrorTitle", {
          type: AlertType.Danger,
          message: "processingErrorMessage",
          primaryBtn: {
            text: "close",
            action: () => closeInfoDialog(),
          },
        });
      }
    } else {
      clearFormValues(["invoicePaymentTypeCode", "overpaidPaymentTypeCode"]);
    }

    validateFormField(name, value);
  };

  // sub-renders
  const renderDepositPaymentTypes = (name: string) => {
    return paymentTypes.map((type) => renderRadioBox(type, name));
  };

  const renderInvoicePaymentTypes = (name: string) => {
    const notAllowedTypes: string[] = ["Sipo", "DirectDebitOrder"];
    const invoiceTypes = paymentTypes.filter(
      (x) => !notAllowedTypes.includes(x)
    );

    return invoiceTypes.map((type) => renderRadioBox(type, name));
  };

  const renderRadioBox = (paymentType: string, name: string) => {
    return (
      <div className="col-md-3 px-2" key={paymentType}>
        <RadioBox
          label={tLabels(`paymentType.${paymentType}`)}
          icon={{ icon: "coins" }}
          className="mb-lg-0"
          name={name}
          value={paymentType}
          onChange={paymentTypeChangeHandler}
          checked={getFormValue(name) === paymentType}
        />
      </div>
    );
  };

  // render
  if (onlyInvoicePaymentTypes === false) {
    return (
      <PageCard
        title={tLabels("depositPaymentTypeCardTitle")}
        bodyClassName="pt-4 px-4"
        cardClassName="mb-4"
      >
        <FormGroup
          labelText={tLabels("depositPaymentTypeCode")}
          validationError={errors["depositPaymentTypeCode"]}
        >
          <div className="row mx-n2">
            {renderDepositPaymentTypes("depositPaymentTypeCode")}
          </div>
        </FormGroup>
      </PageCard>
    );
  } else {
    return (
      <PageCard
        title={tLabels("invoicePaymentTypeCardTitle")}
        bodyClassName="pt-4 px-4"
        cardClassName="mb-4"
      >
        <FormGroup
          labelText={tLabels("invoicePaymentTypeCode")}
          validationError={errors["invoicePaymentTypeCode"]}
        >
          <div className="row mx-n2">
            {renderInvoicePaymentTypes("invoicePaymentTypeCode")}
          </div>
        </FormGroup>
      </PageCard>
    );
  }
  
};

export default PaymentMethodTypeSubForm;
